import { useTranslation } from "react-i18next";
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../../contexts/AuthContext";
import { useCompatibilityChat } from "./useCompatibilityChat";
import {
  ArrowLeft,
  Send,
  Heart,
  Sparkles,
  User,
  MoreVertical,
} from "lucide-react";
import { getAppLanguage, toLocale } from "../../utils/appLanguage";

interface LocationState {
  otherUserName?: string;
}

export const CompatibilityChatScreen: React.FC = () => {
  const { t } = useTranslation();
  const { requestId } = useParams<{ requestId: string }>();
  const location = useLocation();
  const otherUserName =
    (location.state as LocationState | null)?.otherUserName || t("social.chat.yourMatch");

  const { user } = useAuth();
  const { messages, loading, sending, sendMessage } =
    useCompatibilityChat(requestId);

  const [draft, setDraft] = useState("");
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text) return;
    setDraft("");
    try {
      await sendMessage(text);
    } catch (error) {
      toast.error(t("social.chat.sendFailed"));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (date: string) => {
    return new Date(date).toLocaleTimeString(toLocale(getAppLanguage()), {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="h-screen flex flex-col bg-[#0f0a14] relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-[#a78bff]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-[#a78bff]/10 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#a78bff]/10 rounded-full blur-3xl"></div>

      {/* Fixed Header */}
      <div className="relative z-10 bg-[#1a131f] backdrop-blur-xl border-b border-white/10 shadow-lg shadow-black/30 flex-shrink-0">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/history"
              className="p-2 hover:bg-white/10 rounded-full transition-all duration-200 group"
            >
              <ArrowLeft className="w-5 h-5 text-[#c8bfd1] group-hover:text-rose-400 transition-colors" />
            </Link>

            <div className="relative">
              <div className="w-11 h-11 rounded-full bg-[#a78bff] flex items-center justify-center text-[#0f0a14] text-base font-bold shadow-lg shadow-black/30">
                {otherUserName.charAt(0).toUpperCase()}
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-400 rounded-full border-2 border-white/10"></div>
            </div>

            <div>
              <h1 className="text-base font-semibold text-[#f7f3fa]">
                {otherUserName}
              </h1>
              <p className="text-xs text-green-500 flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full inline-block"></span>
                {t("social.chat.online")}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button className="p-2 hover:bg-white/10 rounded-full transition-all duration-200">
              <Heart className="w-5 h-5 text-[#c8bfd1] hover:text-rose-400 transition-colors" />
            </button>
            <button className="p-2 hover:bg-white/10 rounded-full transition-all duration-200">
              <MoreVertical className="w-5 h-5 text-[#c8bfd1] hover:text-rose-400 transition-colors" />
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable Messages Area */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-3 relative z-10"
        style={{
          scrollBehavior: "smooth",
          maxHeight: "calc(100vh - 140px)",
        }}
      >
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="relative">
              <div className="w-10 h-10 border-4 border-rose-400/30 rounded-full"></div>
              <div className="absolute top-0 left-0 w-10 h-10 border-4 border-rose-400 rounded-full border-t-transparent animate-spin"></div>
            </div>
          </div>
        )}

        {!loading && messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center py-12">
            <div className="w-20 h-20 bg-[#a78bff]/15 rounded-full flex items-center justify-center mb-4">
              <Sparkles className="w-8 h-8 text-rose-400" />
            </div>
            <h3 className="text-lg font-semibold text-[#f7f3fa] mb-2">
              {t("social.chat.startTitle")}
            </h3>
            <p className="text-sm text-[#c8bfd1] max-w-xs">
              {t("social.chat.startBody", { name: otherUserName })}
            </p>
          </div>
        )}

        {messages.map((msg, index) => {
          const isMine = msg.senderId === user?._id;
          const showTime =
            index === 0 ||
            (index > 0 &&
              new Date(msg.createdAt).getTime() -
                new Date(messages[index - 1].createdAt).getTime() >
                300000);

          return (
            <div key={msg._id} className="space-y-1">
              {showTime && (
                <div className="flex justify-center">
                  <span className="text-xs text-[#c8bfd1] bg-[#1a131f]/5 px-3 py-1 rounded-full backdrop-blur-sm">
                    {formatTime(msg.createdAt)}
                  </span>
                </div>
              )}
              <div
                className={`flex ${isMine ? "justify-end" : "justify-start"} animate-fadeIn`}
              >
                {!isMine && (
                  <div className="w-8 h-8 rounded-full bg-[#a78bff] flex items-center justify-center text-[#0f0a14] text-xs font-bold mr-2 flex-shrink-0 shadow-lg shadow-black/30">
                    {otherUserName.charAt(0).toUpperCase()}
                  </div>
                )}
                <div
                  className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm relative ${
                    isMine
                      ? "bg-[#a78bff] text-[#0f0a14] rounded-br-sm shadow-lg shadow-black/30"
                      : "bg-[#1a131f] backdrop-blur-sm text-[#f7f3fa] rounded-bl-sm shadow-md shadow-black/30 border border-white/10"
                  }`}
                >
                  {msg.text}
                  <div
                    className={`text-[10px] mt-1 opacity-60 ${
                      isMine ? "text-white/80 text-right" : "text-[#c8bfd1]"
                    }`}
                  >
                    {formatTime(msg.createdAt)}
                  </div>
                </div>
                {isMine && (
                  <div className="w-8 h-8 rounded-full bg-[#a78bff] flex items-center justify-center text-[#0f0a14] text-xs font-bold ml-2 flex-shrink-0 shadow-lg shadow-purple-200">
                    <User className="w-4 h-4" />
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Fixed Footer with Input */}
      <div className="relative z-10 bg-[#1a131f] backdrop-blur-xl border-t border-white/10 shadow-lg shadow-black/30 flex-shrink-0">
        <div className="px-4 py-3 flex items-center gap-3">
          <div className="flex-1 relative">
            <input
              type="text"
              className="w-full bg-[#221a29]/80 text-[#f7f3fa] placeholder:text-[#c8bfd1]/60 [color-scheme:dark] rounded-2xl px-5 py-3 text-sm border-2 border-white/10 focus:border-[#a78bff] focus:outline-none focus:ring-4 focus:ring-[#a78bff]/20 transition-all duration-200 placeholder:text-[#c8bfd1] pr-12"
              placeholder={t("social.chat.placeholder")}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={sending}
            />
            {draft.trim() && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <span className="text-xs text-rose-400 font-medium animate-pulse">
                  ●
                </span>
              </div>
            )}
          </div>
          <button
            type="button"
            className={`flex items-center justify-center w-12 h-12 rounded-2xl transition-all duration-200 shadow-lg ${
              sending || !draft.trim()
                ? "bg-[#2a2133] text-[#c8bfd1] cursor-not-allowed shadow-black/30"
                : "bg-[#a78bff] text-[#0f0a14] hover:scale-105 active:scale-95 shadow-black/30 hover:brightness-110 hover:shadow-black/30"
            }`}
            onClick={handleSend}
            disabled={sending || !draft.trim()}
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Add animation styles */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default CompatibilityChatScreen;
